import type { NavigationProps, NavItemButton, NavItemLink, NavSection } from "@wayflyer/flyui";
import {
  IconArrowLeftTrayRight16Line,
  IconArrowsRightLeft16Line,
  IconArrowTrendingUp16Line,
  IconBanknotes16Line,
  IconBenchmarks16Line,
  IconBookOpen16Line,
  IconBuildingBank16Line,
  IconBuildingOffice16Line,
  IconChartBars16Line,
  IconCheckSquares16Line,
  IconCoinDollar16Line,
  IconDocument16Line,
  IconGear16Line,
  IconGift16Line,
  IconPaymentCard16Line,
  IconPeople216Line,
  IconPersonPlus16Line,
  IconQuestionMarkCircle16Line,
  IconWrench16Line,
} from "@wayflyer/flyui-icons/16/line";
import { BaseSchemas } from "api-types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import type { NavigateOptions } from "react-router-dom";
import { useInviteModalActions } from "shared-ui";

import { MAX_VISIBLE_ACCOUNTS } from "../../../banking/constants";

import { getNavItemNameFromId } from "./nav-item-strings";
import { useNavTaskNotificationCount } from "./nav-task-notification-count";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}
function getIconComponent(icon: string): ReactNode | undefined {
  if (typeof icon === "string") {
    switch (icon) {
      case "bank":
        return <IconBanknotes16Line />;
      case "line-chart":
        return <IconChartBars16Line />;
      case "team":
        return <IconPersonPlus16Line />;
      case "help":
        return <IconQuestionMarkCircle16Line />;
      case "connections":
        return <IconArrowsRightLeft16Line />;
      case "presentation-chart-line":
        return <IconChartBars16Line />;
      case "benchmarks":
        return <IconBenchmarks16Line />;
      case "banking":
        return <IconBuildingOffice16Line />;
      case "financial-os":
        return <IconBenchmarks16Line />; // TODO: update
      case "internal":
        return <IconWrench16Line />;
      case "dollar":
        return <IconCoinDollar16Line />;
      case "share":
        return <IconGift16Line />;
      case "account":
        return <IconBuildingBank16Line />;
      case "payments":
        return <IconArrowTrendingUp16Line />;
      case "recipients":
        return <IconPeople216Line />;
      case "cards":
        return <IconPaymentCard16Line />;
      case "transactions":
        return <IconBookOpen16Line />;
      case "documents":
        return <IconDocument16Line />;
      case "tasks":
        return <IconCheckSquares16Line />;
      default:
        return;
    }
  }
}

const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

export const useNavigationItems = ({
  appConfig,
  clientNavigateFn,
}: {
  appConfig: BaseSchemas.CustomerAppConfig;
  clientNavigateFn: (path: string, options?: NavigateOptions) => void;
}): NavigationProps => {
  const intl = useIntl();
  const { navItems, customerDomain } = appConfig;
  const taskNotifications = useNavTaskNotificationCount(customerDomain);
  const enableClientSideLinks = useFeatureFlagEnabled("enable-all-client-side-customer-app-links");
  const { openInviteModal } = useInviteModalActions();
  const alternativePathsMap = {};

  const linkBehavior = enableClientSideLinks
    ? {}
    : {
        navigateFn: clientNavigateFn,
      };

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): Array<NavItemButton | NavItemLink> | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    return navItems.map(({ id, icon, path }) => {
      return {
        id,
        href: path,
        icon: getIconComponent(icon),
        label: getNavItemNameFromId(intl, id),
        ...linkBehavior,
      };
    });
  }

  const profileSection: NavigationProps["profileSection"] = {
    label: appConfig.customerName,
    id: "profile-section",
    icon: null,
    items: [
      {
        id: "settings",
        label: intl.formatMessage({ id: "Menu: Settings", defaultMessage: "Settings" }),
        href: `/${appConfig?.customerDomain}/settings`,
        icon: <IconBuildingOffice16Line />,
        ...linkBehavior,
      },
      {
        id: "invite",
        label: intl.formatMessage({ id: "Invite someone", defaultMessage: "Invite a team member" }),
        onClick: () => openInviteModal(),
        icon: <IconPeople216Line />,
      },
      ...(!navItems || navItems.length === 0
        ? [
            {
              id: "help",
              label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
              href: "https://www.wayflyer.com/help-center",
              external: true,
              icon: <IconQuestionMarkCircle16Line />,
            },
          ]
        : []),
      ...(window?.Optanon?.ToggleInfoDisplay
        ? [
            {
              id: "privacy-settings",
              label: intl.formatMessage({
                id: "Privacy settings",
                defaultMessage: "Privacy settings",
              }),
              onClick: () => window.Optanon.ToggleInfoDisplay(),
              icon: <IconGear16Line />,
            },
          ]
        : []),
      {
        id: "sign-out",
        label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
        error: true,
        href: "/authn/logout",
        reloadDocument: enableClientSideLinks ? true : undefined,
        icon: <IconArrowLeftTrayRight16Line />,
      },
    ],
  };

  const NAVIGABLE_SECTIONS = ["banking/payments", "banking/accounts"];

  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    let items = getClientSubNavItems(navItem.subItems);
    const icon = getIconComponent(navItem.icon);
    const label = getNavItemNameFromId(intl, navItem.id);
    const navigableSection = navItem.path && NAVIGABLE_SECTIONS.some((section) => navItem.path.includes(section));

    const isAccountsSection = id.includes("accounts") || navItem.path?.includes("/accounts");

    if (isAccountsSection && Array.isArray(items) && items.length > 0) {
      if (items.length > MAX_VISIBLE_ACCOUNTS) {
        const visibleItems = items.slice(0, MAX_VISIBLE_ACCOUNTS);
        const remainingCount = items.length - MAX_VISIBLE_ACCOUNTS;

        const seeMoreItem: NavItemLink = {
          id: "banking-accounts-see-more",
          label: `See more (${remainingCount})`,
          href: "#",
          icon: undefined,
          navigateFn: () => {
            const accountsPath = navItem.path || "";
            clientNavigateFn(`${accountsPath}?expand=true`);
          },
        };

        items = [...visibleItems, seeMoreItem];
      }

      if (items.length === 1) {
        const singleAccountItem = items[0];
        const accountHref = "href" in singleAccountItem ? singleAccountItem.href : navItem.path;
        return {
          id,
          icon,
          label,
          href: accountHref,
          ...linkBehavior,
        };
      }
    }

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
          ...linkBehavior,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
        ...linkBehavior,
      };
    }
  };

  const sections: NavigationProps["sections"] = {
    navItems: (navItems ?? []).map(toClientNavItem).filter((item) => !item.href?.includes("customer-referrals")), //@todo: do this on the server,
  };

  const infoItems = [
    ...(navItems ?? []).map(toClientNavItem).filter((item) => item.href?.includes("customer-referrals")), //@todo: do this on the server,
    ...(navItems && navItems.length !== 0
      ? [
          {
            id: "help",
            label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
            href: "https://www.wayflyer.com/help-center",
            icon: <IconQuestionMarkCircle16Line />,
            external: true,
          },
        ]
      : []),
  ] as Array<NavItemLink>;

  const notificationBubble =
    taskNotifications > 0
      ? {
          navItemId: "customer tasks page title",
          count: taskNotifications,
          title: "customer tasks page title",
        }
      : undefined;

  return {
    profileSection,
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubble,
  };
};
